import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState,
} from 'react';

interface HeaderSlotValue {
    /** Hueco de la cabecera compartida donde las páginas pintan su título. */
    slot: HTMLElement | null;
    setSlot: (element: HTMLElement | null) => void;
    /** True cuando una página ya está pintando su título en el hueco. */
    hasPageTitle: boolean;
    setHasPageTitle: (hasPageTitle: boolean) => void;
}

const HeaderSlotContext = createContext<HeaderSlotValue | null>(null);

/**
 * Expone el hueco de la cabecera compartida. Las páginas (SttPage) pintan ahí
 * su título con un portal en lugar de publicar props en un contexto: así el
 * título siempre está al día aunque dependa de datos que llegan tarde, y no hay
 * ciclos de re-render por props inestables (subtítulos JSX, callbacks inline).
 *
 * Las páginas que no usan SttPage no pintan nada en el hueco; para ésas la
 * cabecera cae al título que publicaron con `setHeader()`.
 */
export function HeaderProvider({
    children,
}: {
    children: React.ReactNode;
}): React.ReactElement {
    const [slot, setSlotState] = useState<HTMLElement | null>(null);
    const [hasPageTitle, setHasPageTitleState] = useState<boolean>(false);

    const setSlot = useCallback((element: HTMLElement | null): void => {
        setSlotState(element);
    }, []);

    const setHasPageTitle = useCallback((value: boolean): void => {
        setHasPageTitleState(value);
    }, []);

    const value = useMemo<HeaderSlotValue>(
        () => ({ slot, setSlot, hasPageTitle, setHasPageTitle }),
        [slot, setSlot, hasPageTitle, setHasPageTitle],
    );

    return (
        <HeaderSlotContext.Provider value={value}>
            {children}
        </HeaderSlotContext.Provider>
    );
}

export function useHeaderSlot(): HeaderSlotValue {
    const value = useContext(HeaderSlotContext);

    if (!value) {
        throw new Error('useHeaderSlot must be used inside a HeaderProvider');
    }

    return value;
}

/**
 * Igual que `useHeaderSlot` pero devuelve null fuera del layout del gestor
 * (las páginas públicas no tienen cabecera compartida).
 */
export function useOptionalHeaderSlot(): HeaderSlotValue | null {
    return useContext(HeaderSlotContext);
}
